import { Phone, MessageCircle, Instagram, Facebook, Mail, Globe } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useI18n } from "@/lib/i18n";

const CARDS = [
    { id: "whatsapp", href: `https://wa.me/${BRAND.whatsappRaw}`, value: BRAND.whatsapp, icon: MessageCircle, external: true, accent: true },
    { id: "email",    href: `mailto:${BRAND.email}`,               value: BRAND.email,    icon: Mail,           external: false, accent: true },
    { id: "phone",    href: `tel:${BRAND.phoneRaw}`,               value: BRAND.phone,    icon: Phone,          external: false },
    { id: "instagram",href: BRAND.instagramUrl,                    value: `@${BRAND.instagram}`, icon: Instagram, external: true },
    { id: "facebook", href: BRAND.facebookUrl,                     value: BRAND.facebook, icon: Facebook,       external: true },
    { id: "website",  href: BRAND.websiteUrl,                      value: BRAND.website,  icon: Globe,          external: true },
];

export default function ContactSection() {
    const { t } = useI18n();

    return (
        <section id="contact" data-testid="contact-section" className="relative py-28 md:py-40">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="reveal max-w-3xl">
                    <span className="eyebrow">{t("contact.eyebrow")}</span>
                    <h2 className="font-serif-display text-white text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
                        {t("contact.title_a")} <em className="not-italic text-[#C9A96E]">{t("contact.title_highlight")}</em>
                    </h2>
                    <p className="mt-8 text-slate-400 max-w-2xl leading-relaxed">{t("contact.subtitle")}</p>
                </div>

                <div
                    data-testid="contact-cards-grid"
                    className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                >
                    {CARDS.map((c, i) => {
                        const Icon = c.icon;
                        const onClick = (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            try {
                                if (c.external) {
                                    const w = window.open(c.href, "_blank", "noopener,noreferrer");
                                    if (!w) window.location.href = c.href;
                                } else {
                                    window.location.href = c.href;
                                }
                            } catch (_) {
                                window.location.href = c.href;
                            }
                        };
                        return (
                            <a
                                key={c.id}
                                href={c.href}
                                target={c.external ? "_blank" : undefined}
                                rel={c.external ? "noopener noreferrer" : undefined}
                                data-testid={`contact-link-${c.id}`}
                                aria-label={`${t(`contact.cards.${c.id}`)} — ${c.value}`}
                                onClick={onClick}
                                onAuxClick={onClick}
                                role="link"
                                tabIndex={0}
                                style={{ transitionDelay: `${i * 60}ms` }}
                                className={`reveal glass-card p-8 md:p-9 flex flex-col items-start gap-5 group cursor-pointer no-underline select-none [-webkit-tap-highlight-color:rgba(201,169,110,0.25)] touch-manipulation ${
                                    c.accent ? "border-[#C9A96E]/45 hover:border-[#C9A96E]" : ""
                                }`}
                            >
                                <div
                                    className={`pointer-events-none h-14 w-14 rounded-full flex items-center justify-center transition-colors duration-500 ${
                                        c.accent
                                            ? "bg-[#C9A96E] text-[#050D1C] group-hover:bg-[#B8925A]"
                                            : "border border-[#C9A96E]/40 text-[#C9A96E] group-hover:bg-[#C9A96E] group-hover:text-[#050D1C]"
                                    }`}
                                >
                                    <Icon size={22} strokeWidth={1.6} />
                                </div>
                                <div className="pointer-events-none min-w-0 w-full">
                                    <div className="text-[10px] tracking-[0.32em] uppercase text-slate-400">
                                        {t(`contact.cards.${c.id}`)}
                                    </div>
                                    <div className="mt-2 font-serif-display text-white text-xl md:text-2xl leading-tight break-words">
                                        {c.value}
                                    </div>
                                </div>
                                <div className="pointer-events-none mt-auto pt-4 border-t border-[#C9A96E]/15 text-[11px] tracking-[0.28em] uppercase text-[#C9A96E] w-full">
                                    {t("contact.open")} →
                                </div>
                            </a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
